import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, PlayCircle, Pencil, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import useLoad from "@/lib/useLoad";
import ExercisePicker from "@/components/exercises/ExercisePicker";
import DemoDialog, { findDemo } from "@/components/exercises/DemoDialog";
import ExerciseDialog from "@/components/exercises/ExerciseDialog";

const EXERCISE_PROPERTIES = [
  { key: "sets", label: "Sets" },
  { key: "reps", label: "Reps" },
  { key: "rest", label: "Rest" },
  { key: "weight", label: "Weight" },
  { key: "time", label: "Duration" },
  { key: "distance", label: "Distance" },
];
const blankEx = { name: "", sets: 3, reps: "12", rest: "60 sec", weight: 15, weight_unit: "kg", time: "", distance: "", notes: "", properties: ["sets", "reps", "weight", "rest"] };
const MAX_DAYS = 7;
const blankMeal = { name: "", items: "", calories: 0, protein: 0, carbs: 0, fat: 0 };
const cell = "h-9 bg-[#111827] text-sm";
const FIELD_WIDTH = "w-32";
const SET_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const REP_OPTIONS = [...Array.from({ length: 30 }, (_, i) => String(i + 1)), "8-10", "8-12", "10-12", "12-15", "15-20", "AMRAP"];
const REST_UNITS = [{ value: "sec", label: "sec", step: 5 }, { value: "min", label: "min", step: 0.5 }];
const DISTANCE_UNITS = [{ value: "ft", label: "ft", step: 1 }, { value: "m", label: "m", step: 1 }, { value: "km", label: "km", step: 0.25 }];
const joinedField = "h-9 rounded-none border-0 bg-transparent px-0 shadow-none focus-visible:ring-0";
const joinedFieldGroup = "flex h-9 min-w-0 overflow-hidden rounded-md border border-input bg-[#111827] focus-within:border-[#FF3B30] focus-within:ring-1 focus-within:ring-[#FF3B30]";
const stepperButton = "h-9 w-6 shrink-0 border-0 bg-transparent px-0 text-sm text-slate-300 hover:bg-[#1F293D] hover:text-white";

function getExerciseProperties(exercise) {
  if (Array.isArray(exercise.properties)) return exercise.properties.filter((key) => key !== "notes");
  return EXERCISE_PROPERTIES.filter(({ key }) => {
    const value = key === "time" ? exercise.time : exercise[key];
    return value !== "" && value != null;
  }).map(({ key }) => key);
}

function parseMeasure(value, units, defaultUnit) {
  const raw = String(value ?? "").trim();
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]*)$/);
  if (!match) return { value: raw, unit: defaultUnit };
  const suffix = match[2].toLowerCase();
  const aliases = { s: "sec", second: "sec", seconds: "sec", mins: "min", minute: "min", minutes: "min", feet: "ft", foot: "ft", meter: "m", meters: "m", metre: "m", metres: "m", kilometer: "km", kilometers: "km", kilometre: "km", kilometres: "km" };
  const candidate = aliases[suffix] || suffix;
  return { value: match[1], unit: units.some((unit) => unit.value === candidate) ? candidate : defaultUnit };
}

function formatMeasure(value, unit) {
  return value === "" || value == null ? "" : `${value} ${unit}`;
}

function StepperInput({ value, step, onChange, label, testid, unit }) {
  const adjust = (direction) => {
    const current = Number(value);
    const stepPrecision = (String(step).split(".")[1] || "").length;
    const valuePrecision = (String(value ?? "").split(".")[1] || "").length;
    const precision = Math.max(stepPrecision, valuePrecision);
    const scale = 10 ** precision;
    const stepUnits = Math.round(step * scale);
    const currentUnits = Math.round((Number.isFinite(current) ? current : 0) * scale);
    const remainder = ((currentUnits % stepUnits) + stepUnits) % stepUnits;
    const adjustment = remainder === 0 ? direction * stepUnits : direction > 0 ? stepUnits - remainder : -remainder;
    onChange(Math.max(0, currentUnits + adjustment) / scale);
  };
  return (
    <div className={joinedFieldGroup}>
      <button type="button" onClick={() => adjust(-1)} className={stepperButton} aria-label={`Decrease ${label}`}>-</button>
      <Input type="number" min="0" step={step} value={value ?? ""} placeholder="0" onChange={(event) => onChange(event.target.value)} className={`${joinedField} [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none min-w-0 flex-1 text-center`} aria-label={label} data-testid={testid} />
      {unit && (
        <select value={unit.value} onChange={(event) => unit.onChange(event.target.value)} aria-label={`${label} unit`} className="h-9 w-8 shrink-0 cursor-pointer appearance-none bg-transparent text-center text-xs text-slate-300 focus:outline-none [&>option]:bg-[#0B1220]" data-testid={testid && `${testid}-unit`}>
          {unit.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      )}
      <button type="button" onClick={() => adjust(1)} className={stepperButton} aria-label={`Increase ${label}`}>+</button>
    </div>
  );
}

function MeasureInput({ label, value, units, defaultUnit, onChange, testid, width }) {
  const parsed = parseMeasure(value, units, defaultUnit);
  const selectedUnit = units.find((unit) => unit.value === parsed.unit);
  return (
    <div className={`${width} min-w-0`}>
      <div className="mb-1 flex h-5 items-center pr-5">
        <span className="text-[10px] text-slate-500">{label}</span>
      </div>
      <StepperInput value={parsed.value} step={selectedUnit?.step || 1} onChange={(next) => onChange(formatMeasure(next, parsed.unit))} label={label} testid={testid}
        unit={{ value: parsed.unit, options: units.map((item) => item.value), onChange: (next) => onChange(formatMeasure(parsed.value, next)) }} />
    </div>
  );
}

function AddPropertyButton({ options, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  if (!options.length) return null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0 bg-[#111827]" aria-label="Add property" data-testid={testid}><Plus size={14} /></Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 border-[#1E293B] bg-[#0B1220] p-1">
        {options.map(({ key, label }) => (
          <button key={key} type="button" onClick={() => { onSelect(key); setOpen(false); }} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-white/5" data-testid={`${testid}-${key}`}>{label}</button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function useDays(content, onChange, fixedDayCount = 0) {
  const savedDays = content.days || [];
  const days = fixedDayCount
    ? Array.from({ length: fixedDayCount }, (_, index) => savedDays[index] || { name: `Day ${index + 1}`, exercises: [] })
    : savedDays;
  const setDays = (d) => onChange({ ...content, days: fixedDayCount ? d.slice(0, fixedDayCount) : d });
  const updDay = (i, patch) => setDays(days.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return { days, setDays, updDay };
}

function exerciseDetails(library, exercise) {
  const item = findDemo(library, exercise);
  if (!item) return "";
  return [
    item.primary_muscle || item.muscle_group,
    ...(item.secondary_muscles || []).map((muscle) => muscle.name),
    ...(item.equipment_items || []).map((equipment) => equipment.name),
    ...(item.exercise_categories || []).map((category) => category.name),
  ].filter(Boolean).join(" · ");
}

function DemoButton({ demo, onOpen, testid }) {
  if (!demo?.media) return <span className="w-8 shrink-0" />;
  return (
    <button type="button" onClick={() => onOpen(demo)} title="Watch demo" className="w-8 h-8 -my-1 grid place-items-center text-[#FF3B30] hover:text-white transition-colors shrink-0" data-testid={testid}>
      <PlayCircle size={16} />
    </button>
  );
}

export function WorkoutEditor({ content, savedContent, onChange }) {
  const { days, setDays, updDay } = useDays(content, onChange, MAX_DAYS);
  const [library, , setLibrary] = useLoad("/exercises");
  const daysRef = useRef(days);
  daysRef.current = days;
  const syncedLibraryRef = useRef(null);
  const [demo, setDemo] = useState(null);
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const firstIncompleteDay = days.findIndex((day) => !(day.exercises || []).some((exercise) => String(exercise.name || "").trim()));
  const maxEnabledDay = firstIncompleteDay === -1 ? days.length - 1 : firstIncompleteDay;
  const currentDay = Math.min(activeDay, maxEnabledDay);
  useEffect(() => {
    if (activeDay > maxEnabledDay) setActiveDay(maxEnabledDay);
  }, [activeDay, maxEnabledDay]);
  useEffect(() => {
    if (!library || syncedLibraryRef.current === library) return;
    syncedLibraryRef.current = library;
    const exercisesById = new Map(library.map((exercise) => [exercise.id, exercise]));
    let changed = false;
    const syncedDays = daysRef.current.map((day) => ({
      ...day,
      exercises: (day.exercises || []).map((exercise) => {
        const libraryExercise = exercisesById.get(exercise.exercise_id);
        if (!libraryExercise || (exercise.notes || "") === (libraryExercise.instructions || "")) return exercise;
        changed = true;
        return { ...exercise, notes: libraryExercise.instructions || "" };
      }),
    }));
    if (changed) setDays(syncedDays);
  }, [library, setDays]);

  const updRow = (di, ei, patch) => updDay(di, { exercises: days[di].exercises.map((e, j) => (j === ei ? { ...e, ...patch } : e)) });
  const updEx = (di, ei, k, v) => updRow(di, ei, k === "name" ? { name: v, exercise_id: null } : { [k]: v });
  const pickerHandles = useRef({});
  const rowSnapshots = useRef({});
  const snapshotRow = (di, ei) => {
    const key = `${di}-${ei}`;
    if (rowSnapshots.current[key]) return;
    const { name, notes, exercise_id } = days[di].exercises[ei];
    rowSnapshots.current[key] = { name, notes, exercise_id };
  };
  const revertRow = (di, ei) => {
    const key = `${di}-${ei}`;
    const snapshot = rowSnapshots.current[key];
    if (!snapshot) return;
    delete rowSnapshots.current[key];
    updRow(di, ei, snapshot);
  };
  const clearRow = (di, ei) => {
    const saved = savedContent?.days?.[di]?.exercises?.[ei];
    if (saved?.name) {
      const { name, notes, exercise_id } = saved;
      rowSnapshots.current[`${di}-${ei}`] = { name, notes, exercise_id };
    } else snapshotRow(di, ei);
    updRow(di, ei, { name: "", notes: "", exercise_id: null });
    pickerHandles.current[`${di}-${ei}`]?.focus();
  };
  const toggleExProperty = (di, ei, key, checked) => {
    const selected = getExerciseProperties(days[di].exercises[ei]);
    updRow(di, ei, { properties: checked ? [...new Set([...selected, key])] : selected.filter((property) => property !== key) });
  };
  const remainingProperties = (ex) => EXERCISE_PROPERTIES.filter(({ key }) => !getExerciseProperties(ex).includes(key));
  const renderProperty = (key, di, ei, ex) => {
    let field = null;
    if (key === "sets") field = (
      <div className="w-20 min-w-0">
        <span className="mb-1 block text-[10px] text-slate-500">Sets</span>
        <Select value={ex.sets == null || ex.sets === "" ? "none" : String(ex.sets)} onValueChange={(v) => updEx(di, ei, "sets", v === "none" ? "" : Number(v))}>
          <SelectTrigger className={`${cell} px-2`} aria-label="Sets"><SelectValue placeholder="Sets" /></SelectTrigger>
          <SelectContent className="min-w-0">
            <SelectItem value="none">-</SelectItem>
            {!SET_OPTIONS.includes(String(ex.sets)) && ex.sets != null && ex.sets !== "" && <SelectItem value={String(ex.sets)}>{ex.sets} (current)</SelectItem>}
            {SET_OPTIONS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
    if (key === "reps") field = (
      <div className="w-20 min-w-0">
        <span className="mb-1 block text-[10px] text-slate-500">Reps</span>
        <Select value={ex.reps || "none"} onValueChange={(v) => updEx(di, ei, "reps", v === "none" ? "" : v)}>
          <SelectTrigger className={`${cell} px-2`} aria-label="Reps"><SelectValue placeholder="Reps" /></SelectTrigger>
          <SelectContent className="min-w-0 w-[var(--radix-select-trigger-width)]">
            <SelectItem value="none">-</SelectItem>
            {ex.reps && !REP_OPTIONS.includes(String(ex.reps)) && <SelectItem value={String(ex.reps)}>{ex.reps} (current)</SelectItem>}
            {REP_OPTIONS.map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
    if (key === "rest") field = <MeasureInput label="Rest" value={ex.rest} units={REST_UNITS} defaultUnit="sec" onChange={(value) => updEx(di, ei, "rest", value)} testid={`ex-rest-${di}-${ei}`} width={FIELD_WIDTH} />;
    if (key === "weight") field = (
      <div className={`${FIELD_WIDTH} min-w-0`}>
        <div className="mb-1 flex h-5 items-center pr-5">
          <span className="text-[10px] text-slate-500">Weight</span>
        </div>
        <StepperInput value={ex.weight ?? ""} step={0.5} onChange={(next) => updEx(di, ei, "weight", next === "" ? "" : Number(next))} label="Weight" testid={`ex-weight-${di}-${ei}`}
          unit={{ value: ex.weight_unit || "kg", options: ["kg", "lb"], onChange: (next) => updEx(di, ei, "weight_unit", next) }} />
      </div>
    );
    if (key === "time") field = <MeasureInput label="Duration" value={ex.time} units={REST_UNITS} defaultUnit="sec" onChange={(value) => updEx(di, ei, "time", value)} testid={`ex-duration-${di}-${ei}`} width={FIELD_WIDTH} />;
    if (key === "distance") field = <MeasureInput label="Distance" value={ex.distance} units={DISTANCE_UNITS} defaultUnit="m" onChange={(value) => updEx(di, ei, "distance", value)} testid={`ex-distance-${di}-${ei}`} width={FIELD_WIDTH} />;
    if (!field) return null;
    return (
      <div key={key} className="relative">
        {field}
        <button type="button" onClick={() => toggleExProperty(di, ei, key, false)} aria-label={`Remove ${key} property`} className="absolute right-0 top-0 grid h-5 w-5 place-items-center text-slate-500 hover:text-white" data-testid={`ex-property-remove-${key}-${di}-${ei}`}><X size={12} /></button>
      </div>
    );
  };
  return (
    <div data-testid="workout-editor">
      <div className="flex flex-wrap items-end gap-1" role="tablist" data-testid="workout-day-tabs">
        {days.map((d, di) => (
          <button key={di} type="button" role="tab" aria-selected={di === currentDay} disabled={di > maxEnabledDay} onClick={() => setActiveDay(di)}
            className={`relative h-10 max-w-[10rem] truncate rounded-t-lg border border-b-0 px-4 text-sm font-medium transition-colors ${di === currentDay ? "z-10 -mb-px border-[#1E293B] border-t-[#FF3B30] bg-[#161E2E] text-white" : di > maxEnabledDay ? "cursor-not-allowed border-transparent bg-[#0B1220] text-slate-700" : "border-transparent bg-[#0B1220] text-slate-400 hover:bg-[#111827] hover:text-white"}`}
            data-testid={`day-tab-${di}`}>Day {di + 1}</button>
        ))}
      </div>
      {days.map((d, di) => di !== currentDay ? null : (
        <div key={di} className="panel rounded-tl-none p-4" role="tabpanel" data-testid={`workout-day-${di}`}>
          {(d.exercises || []).map((ex, ei) => (
            <div key={ei} className="mb-4 space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <label className="w-56 max-w-full min-w-0 shrink-0">
                  <span className="mb-1 block text-[10px] text-slate-500">Exercise</span>
                  <div className="relative">
                    <ExercisePicker pickerRef={(handle) => { pickerHandles.current[`${di}-${ei}`] = handle; }} library={library} value={ex.name} onChange={(value) => updEx(di, ei, "name", value)} onOpen={() => snapshotRow(di, ei)} onRevert={() => revertRow(di, ei)} onPick={(x) => { delete rowSnapshots.current[`${di}-${ei}`]; updRow(di, ei, { name: x.name, notes: x.instructions || "", exercise_id: x.id }); }} onAddNew={(name) => { delete rowSnapshots.current[`${di}-${ei}`]; setAdding({ di, ei, name }); }} className={`${cell} ${library?.some((item) => item.id === ex.exercise_id) ? "pr-16" : ex.name ? "pr-8" : ""} ${ex.exercise_id ? "border-[#FF3B30]/50" : ""}`} testid={`ex-name-${di}-${ei}`} />
                    <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                      {library?.some((item) => item.id === ex.exercise_id) && (
                        <button type="button" onClick={() => setEditing({ di, ei, exercise: library.find((item) => item.id === ex.exercise_id) })} aria-label={`Edit ${ex.name || "exercise"}`} title="Edit exercise" className="grid h-6 w-6 place-items-center text-slate-400 hover:text-white" data-testid={`ex-edit-${di}-${ei}`}><Pencil size={14} /></button>
                      )}
                      {ex.name && (
                        <button type="button" onClick={() => clearRow(di, ei)} aria-label="Clear exercise" title="Clear exercise" className="grid h-6 w-6 place-items-center text-slate-400 hover:text-white" data-testid={`ex-clear-${di}-${ei}`}><X size={14} /></button>
                      )}
                    </div>
                  </div>
                </label>
                {getExerciseProperties(ex).map((key) => renderProperty(key, di, ei, ex))}
                <div className="flex shrink-0 items-center gap-2">
                  <AddPropertyButton options={remainingProperties(ex)} onSelect={(key) => toggleExProperty(di, ei, key, true)} testid={`ex-add-property-${di}-${ei}`} />
                  <Button variant="ghost" size="icon" onClick={() => updDay(di, { exercises: d.exercises.filter((_, j) => j !== ei) })} aria-label={`Remove ${ex.name || "exercise"}`}><Trash2 size={14} /></Button>
                </div>
                <DemoButton demo={findDemo(library, ex)} onOpen={setDemo} testid={`ex-demo-${di}-${ei}`} />
              </div>
              {ex.notes && <p className="text-xs text-slate-400" data-testid={`ex-notes-${di}-${ei}`}>{ex.notes}</p>}
            </div>
          ))}
          <Button variant="ghost" size="sm" className="text-[#FF3B30]" onClick={() => updDay(di, { exercises: [...(d.exercises || []), { ...blankEx }] })} data-testid={`add-exercise-${di}`}>
            <Plus size={14} className="mr-1" /> Exercise
          </Button>
        </div>
      ))}
      <DemoDialog exercise={demo} onClose={() => setDemo(null)} />
      <ExerciseDialog open={Boolean(adding)} initialName={adding?.name || ""} onOpenChange={(o) => !o && setAdding(null)}
        onSaved={(saved) => {
          setLibrary((current) => [...(current || []), saved]);
          updRow(adding.di, adding.ei, { name: saved.name, notes: saved.instructions || "", exercise_id: saved.id });
          setAdding(null);
        }} />
      <ExerciseDialog open={Boolean(editing)} exercise={editing?.exercise} onOpenChange={(o) => !o && setEditing(null)}
        onSaved={(saved) => {
          setLibrary((current) => current?.map((item) => item.id === saved.id ? { ...item, ...saved } : item));
          updRow(editing.di, editing.ei, { name: saved.name, notes: saved.instructions || "", exercise_id: saved.id });
          setEditing(null);
        }} />
    </div>
  );
}

const sum = (meals, k) => meals.reduce((a, m) => a + (Number(m[k]) || 0), 0);

export function MealEditor({ content, onChange }) {
  const { days, setDays, updDay } = useDays(content, onChange);
  const t = content.targets || {};
  const updMeal = (di, mi, k, v) => updDay(di, { meals: days[di].meals.map((m, j) => (j === mi ? { ...m, [k]: v } : m)) });
  return (
    <div className="space-y-5" data-testid="meal-editor">
      <div className="panel p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        {["calories", "protein", "carbs", "fat"].map((k) => (
          <label key={k} className="text-xs">
            <span className="eyebrow">Target {k}{k !== "calories" && " (g)"}</span>
            <Input type="number" value={t[k] ?? ""} onChange={(e) => onChange({ ...content, targets: { ...t, [k]: Number(e.target.value) } })} className={`${cell} mt-1`} data-testid={`target-${k}-input`} />
          </label>
        ))}
      </div>
      {days.map((d, di) => (
        <div key={di} className="panel p-4" data-testid={`meal-day-${di}`}>
          <div className="flex gap-2 mb-3 items-center">
            <Input value={d.name} onChange={(e) => updDay(di, { name: e.target.value })} className={`${cell} font-semibold max-w-xs`} />
            <span className="font-mono-k text-xs text-slate-400 ml-auto" data-testid={`meal-day-total-${di}`}>
              {sum(d.meals || [], "calories")} kcal · P{sum(d.meals || [], "protein")} C{sum(d.meals || [], "carbs")} F{sum(d.meals || [], "fat")}
            </span>
            <Button variant="ghost" size="icon" onClick={() => setDays(days.filter((_, j) => j !== di))}><Trash2 size={15} /></Button>
          </div>
          {(d.meals || []).map((m, mi) => (
            <div key={mi} className="grid grid-cols-4 md:grid-cols-[1.2fr_2.5fr_70px_60px_60px_60px_36px] gap-2 mb-2">
              <Input value={m.name} placeholder="Meal" onChange={(e) => updMeal(di, mi, "name", e.target.value)} className={`${cell} col-span-4 md:col-span-1`} data-testid={`meal-name-${di}-${mi}`} />
              <Input value={m.items} placeholder="Foods & portions" onChange={(e) => updMeal(di, mi, "items", e.target.value)} className={`${cell} col-span-4 md:col-span-1`} />
              {["calories", "protein", "carbs", "fat"].map((k) => (
                <Input key={k} type="number" title={k} value={m[k]} onChange={(e) => updMeal(di, mi, k, Number(e.target.value))} className={cell} />
              ))}
              <Button variant="ghost" size="icon" onClick={() => updDay(di, { meals: d.meals.filter((_, j) => j !== mi) })}><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="text-[#FF3B30]" onClick={() => updDay(di, { meals: [...(d.meals || []), { ...blankMeal }] })} data-testid={`add-meal-${di}`}>
            <Plus size={14} className="mr-1" /> Meal
          </Button>
        </div>
      ))}
      <Button variant="outline" onClick={() => setDays([...days, { name: `Day ${days.length + 1}`, meals: [{ ...blankMeal }] }])} data-testid="add-meal-day-btn">
        <Plus size={15} className="mr-1" /> Add day
      </Button>
    </div>
  );
}

export function PlanView({ plan, onLogDay, library }) {
  const c = plan.content || {};
  const [demo, setDemo] = useState(null);
  if (plan.type === "workout") {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        <DemoDialog exercise={demo} onClose={() => setDemo(null)} />
        {(c.days || []).map((d, i) => (
          <div key={i} className="panel p-5" data-testid={`view-day-${i}`}>
            <div className="flex items-center justify-between">
              <div className="font-display text-xl font-semibold uppercase">{d.name}</div>
              {onLogDay && <Button size="sm" onClick={() => onLogDay(d)} data-testid={`log-day-btn-${i}`}>Log</Button>}
            </div>
            <div className="mt-3 divide-y divide-[#1E293B]">
              {(d.exercises || []).map((e, j) => (
                <div key={j} className="py-2 flex justify-between gap-3 text-sm">
                  <div className="flex items-start gap-2">
                    <DemoButton demo={findDemo(library, e)} onOpen={setDemo} testid={`view-demo-${i}-${j}`} />
                    <div><div>{e.name}</div>{exerciseDetails(library, e) && <div className="text-[11px] text-slate-500">{exerciseDetails(library, e)}</div>}{getExerciseProperties(e).includes("notes") && e.notes && <div className="text-xs text-slate-500">{e.notes}</div>}</div>
                  </div>
                  <div className="font-mono-k text-xs text-slate-300 text-right">{[
                    getExerciseProperties(e).includes("sets") && e.sets && `${e.sets} sets`,
                    getExerciseProperties(e).includes("reps") && e.reps && `${e.reps} reps`,
                    getExerciseProperties(e).includes("weight") && e.weight !== "" && e.weight != null && `${e.weight} ${e.weight_unit || "kg"}`,
                    getExerciseProperties(e).includes("time") && e.time,
                    getExerciseProperties(e).includes("distance") && e.distance,
                    getExerciseProperties(e).includes("rest") && e.rest && `Rest ${e.rest}`,
                  ].filter(Boolean).join(" · ")}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {c.notes && <div className="text-sm text-slate-400 md:col-span-2">{c.notes}</div>}
      </div>
    );
  }
  const t = c.targets || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        {["calories", "protein", "carbs", "fat"].map((k) => (
          <div key={k} className="panel p-3"><div className="eyebrow">{k}</div><div className="font-display text-2xl font-bold">{t[k] ?? "-"}{k !== "calories" && "g"}</div></div>
        ))}
      </div>
      {(c.days || []).map((d, i) => (
        <div key={i} className="panel p-5">
          <div className="font-display text-xl font-semibold uppercase mb-2">{d.name}</div>
          {(d.meals || []).map((m, j) => (
            <div key={j} className="py-2 border-t border-[#1E293B] flex justify-between gap-4 text-sm">
              <div><div className="font-medium">{m.name}</div><div className="text-slate-400 text-xs">{m.items}</div></div>
              <div className="font-mono-k text-xs text-slate-300 whitespace-nowrap">{m.calories} kcal · P{m.protein} C{m.carbs} F{m.fat}</div>
            </div>
          ))}
        </div>
      ))}
      {c.notes && <div className="text-sm text-slate-400">{c.notes}</div>}
    </div>
  );
}
